import type { CancelReplanRow } from "../../../../../_logic/cancel-replan-contract"
import {
  dateFromBatchValues,
  dateMatchesBatchRange,
  textMatchesBatchSearch,
  type BatchDateRangeFilter,
} from "../../../../../../../../_shared/_logic/filter-values"

export type CancelTargetFilterState = {
  search: string
  employee: string
  property: string
  dateRange: BatchDateRangeFilter
}

export type AnnouncementPlanFilterState = {
  search: string
  appointmentStatus: string
  announcementStatus: string
  property: string
  dateRange: BatchDateRangeFilter
}

export const createCancelTargetFilters = (
  overrides: Partial<CancelTargetFilterState> = {},
): CancelTargetFilterState => ({
  search: "",
  employee: "",
  property: "",
  dateRange: { start: "", end: "" },
  ...overrides,
})

export const createAnnouncementPlanFilters = (
  overrides: Partial<AnnouncementPlanFilterState> = {},
): AnnouncementPlanFilterState => ({
  search: "",
  appointmentStatus: "",
  announcementStatus: "",
  property: "",
  dateRange: { start: "", end: "" },
  ...overrides,
})

const employeeLabel = (row: CancelReplanRow) =>
  row.workOrderRow?.currentAppointmentEmployeeLabel ||
  (row.event as { employee?: { display_name?: string | null } } | null)?.employee?.display_name ||
  row.event?.employee_name ||
  ""

const propertyLabel = (row: CancelReplanRow) =>
  row.workOrderRow?.propertyAddressLines?.join(", ") ||
  (row.property as { address?: string | null; label?: string | null } | null)?.address ||
  (row.property as { address?: string | null; label?: string | null } | null)?.label ||
  ""

const appointmentDate = (row: CancelReplanRow) =>
  dateFromBatchValues(
    row.event?.start_at,
    (row.event as { slot_start_at?: string | null } | null)?.slot_start_at,
  )

const appointmentStatus = (row: CancelReplanRow) =>
  row.workOrderRow?.activeAppointmentLabel || row.event?.status || ""

const announcementStatus = (row: CancelReplanRow) =>
  row.activeContactOrder?.status ||
  (row.activeContactOrder as { active_step?: string | null } | null)?.active_step ||
  ""

export const filterCancelTargetRows = (rows: CancelReplanRow[], filters: CancelTargetFilterState) =>
  rows.filter(
    (row) =>
      (!filters.employee || employeeLabel(row) === filters.employee) &&
      (!filters.property || propertyLabel(row) === filters.property) &&
      dateMatchesBatchRange(appointmentDate(row), filters.dateRange) &&
      textMatchesBatchSearch(filters.search, [
        row.workOrder?.order_number,
        row.workOrderRow?.orderSummaryLabel,
        row.workOrderRow?.orderSummarySubLabel,
        row.workOrderRow?.propertyAddressLines?.join(" "),
        row.workOrderRow?.statusLabel,
        row.event?.title,
        employeeLabel(row),
        propertyLabel(row),
      ]),
  )

export const filterAnnouncementPlanRows = (
  rows: CancelReplanRow[],
  filters: AnnouncementPlanFilterState,
) =>
  rows.filter(
    (row) =>
      (!filters.appointmentStatus || appointmentStatus(row) === filters.appointmentStatus) &&
      (!filters.announcementStatus || announcementStatus(row) === filters.announcementStatus) &&
      (!filters.property || propertyLabel(row) === filters.property) &&
      dateMatchesBatchRange(appointmentDate(row), filters.dateRange) &&
      textMatchesBatchSearch(filters.search, [
        row.workOrder?.order_number,
        row.workOrderRow?.orderSummaryLabel,
        row.workOrderRow?.orderSummarySubLabel,
        propertyLabel(row),
        appointmentStatus(row),
        announcementStatus(row),
      ]),
  )

export const cancelReplanEmployeeLabel = employeeLabel
export const cancelReplanPropertyLabel = propertyLabel
export const cancelReplanAppointmentStatus = appointmentStatus
export const cancelReplanAnnouncementStatus = announcementStatus
